import type { EventVisibility } from '../../api/types';

import { parseTimeToMinutes } from './time';

export type EventFormDraft = {
  title: string;
  resourceId: string;
  selectedDate: Date;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  visibility: EventVisibility;
};

export type EventFormContext = {
  today: Date;
  minMaxParticipants: number;
  maxParticipantsLimit: number;
};

export type EventFormErrors = {
  title: string | null;
  date: string | null;
  time: string | null;
  maxParticipants: string | null;
};

export type EventFormValidationResult = {
  errors: EventFormErrors;
  isValid: boolean;
  normalizedTitle: string;
};

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function validateEventFormDraft(draft: EventFormDraft, ctx: EventFormContext): EventFormValidationResult {
  const normalizedTitle = draft.title.trim();

  const titleError = (() => {
    if (normalizedTitle.length < 3) return 'Минимум 3 символа';
    if (normalizedTitle.length > 100) return 'Максимум 100 символов';
    return null;
  })();

  const dateError = (() => {
    const selected = startOfDay(draft.selectedDate);
    const today = startOfDay(ctx.today);
    if (selected.getTime() < today.getTime()) return 'Дата не может быть в прошлом';
    return null;
  })();

  const timeError = (() => {
    const s = parseTimeToMinutes(draft.startTime);
    const e = parseTimeToMinutes(draft.endTime);
    if (s == null || e == null) return 'Укажите время начала и окончания';
    if (e <= s) return 'Время окончания должно быть позже начала';
    if (e - s < 30) return 'Минимальная длительность 30 минут';
    return null;
  })();

  const maxParticipantsError = (() => {
    if (draft.maxParticipants < ctx.minMaxParticipants) {
      return 'Нельзя меньше текущего количества участников';
    }
    if (draft.maxParticipants > ctx.maxParticipantsLimit) return `Максимум ${ctx.maxParticipantsLimit} участников`;
    if (draft.maxParticipants < 2) return 'Минимум 2 участника';
    return null;
  })();

  const isValid =
    titleError == null &&
    normalizedTitle.length >= 3 &&
    normalizedTitle.length <= 100 &&
    draft.resourceId !== '' &&
    dateError == null &&
    timeError == null &&
    maxParticipantsError == null;

  return {
    errors: {
      title: titleError,
      date: dateError,
      time: timeError,
      maxParticipants: maxParticipantsError,
    },
    isValid,
    normalizedTitle,
  };
}
